import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import AddressAutocomplete from "@/components/AddressAutocomplete";

/**
 * ValuationEntry - Subtle, elegant entry point for property valuation
 * Discreet, understated design leading to the full valuation journey
 * Clearly separated from footer with light background treatment
 */
const ValuationEntry = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [address, setAddress] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    // Navigate to valuation page with address as query param
    const params = new URLSearchParams();
    if (address) params.set("address", address);
    navigate(`/estimation?${params.toString()}`);
  };

  return (
    <section className="relative" data-testid="valuation-entry">
      {/* Main content area - light beige background, distinct from footer */}
      <div className="py-10 md:py-14 bg-byron-bg">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {/* Subtle label - Standard section label style */}
            <p className="label-text mb-3">
              {t('valuation.confidentialLabel')}
            </p>

            {/* Main title - understated */}
            <h2 className="heading-primary mb-4">
              {t('valuation.title')}
            </h2>

            {/* Subtle supporting text */}
            <p className="body-text mb-6 max-w-md mx-auto">
              {t('valuation.subtitle')}
            </p>

            {/* Address input + CTA */}
            <form onSubmit={handleSubmit} className="max-w-xl mx-auto">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <AddressAutocomplete
                    value={address}
                    onChange={setAddress}
                    placeholder={t('valuation.addressPlaceholder')}
                    variant="boxed"
                    testId="valuation-address-input"
                  />
                </div>

                <button
                  type="submit"
                  className="flex items-center justify-center gap-2 px-8 py-4 bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.15em] hover:bg-[#2A2A2A] transition-colors whitespace-nowrap"
                  data-testid="valuation-cta"
                >
                  {t('valuation.ctaButton')}
                  <ArrowRight size={14} strokeWidth={1.5} />
                </button>
              </div>
            </form>

            {/* Confidentiality note */}
            <p className="text-xs text-[#999] mt-6">
              {t('valuation.confidentialNote')}
            </p>
          </motion.div>
        </div>
      </div>

      {/* Visual separator from footer */}
      <div className="h-px bg-[#E5E5E0]" />
    </section>
  );
};

export default ValuationEntry;
